import { Box, Container, Flex, Heading, SystemStyleObject } from '@chakra-ui/react';
import { AppLink, Image, LANDING_ROUTES, pxToRem, TRANSITIONS } from '@cyberkongz/ui';
import { SliderNavigationRounded, SliderWrapper } from '@cyberkongz/ui-slider';
import { keyframes } from '@emotion/react';
import { CommunityTab } from '@features/blueprint/CommunityTab';
import { EcosystemTab } from '@features/blueprint/EcosystemTab';
import { IRLTab } from '@features/blueprint/IRLTab';
import { MetaverseTab } from '@features/blueprint/MetaverseTab';
import { SocialImpactTab } from '@features/blueprint/SocialImpactTab';
import { VisionTab } from '@features/blueprint/VisionTab';
import { PageSEOTags } from '@features/seo/SEO';
import { keyBy } from 'lodash-es';
import { useState } from 'react';
import { SwiperSlide } from 'swiper/react';

export const BLUEPRINT_ITEMS = [
  { id: 'vision', slug: 'vision', title: 'vision & principles', description: 'What drives us' },
  { id: 'ecosystem', slug: 'ecosystem', title: 'ecosystem', description: 'What we are' },
  { id: 'metaverse', slug: 'metaverse', title: 'metaverse', description: 'Beyond Metaverse' },
  {
    id: 'community',
    slug: 'community',
    title: 'community',
    description: 'Contributions & Governance',
  },
  { id: 'irl', slug: 'irl', title: 'irl', description: 'Events, Collaborations & More' },
  {
    id: 'social_impact',
    slug: 'social-impact',
    title: 'social impact',
    description: 'Towards Positive Change',
  },
] as const;

type BlueprintItem = typeof BLUEPRINT_ITEMS[number];

export type BlueprintTabId = typeof BLUEPRINT_ITEMS[number]['id'];

const BLUEPRINT_ITEMS_BY_ID = keyBy(BLUEPRINT_ITEMS, 'id') as Record<BlueprintTabId, BlueprintItem>;

const floatAnimation = keyframes`
  0% {
    transform: translate(0);
  }
  50% {
    transform: translate(0px, ${pxToRem(-8)});
  }
  100% {
    transform: translate(0);
  }
`;

function BlueprintTabs({
  activeTab,
  isNavHidden,
}: {
  activeTab: BlueprintTabId;
  isNavHidden: boolean;
}) {
  return (
    <Box
      css={{
        position: 'absolute',
        top: [pxToRem(100), pxToRem(120)],
        left: 0,
        right: 0,
        zIndex: 4,
        transition: TRANSITIONS.standard,
        opacity: isNavHidden ? 0 : 1,
      }}
    >
      <Container>
        <SliderWrapper
          initialSlide={BLUEPRINT_ITEMS.findIndex((item) => item.id === activeTab)}
          slidesPerView="auto"
          slideToClickedSlide
          observer
          observeParents
          css={{
            '& .swiper': {
              px: 10,
            },
            '& .swiper-slide': {
              maxWidth: [pxToRem(140), pxToRem(320)],
              pr: [6, 10],
            },
          }}
        >
          {BLUEPRINT_ITEMS.map(({ id, title, slug, description }) => (
            <SwiperSlide key={id}>
              <AppLink
                href={LANDING_ROUTES.BLUEPRINT_TAB(slug)}
                variant="noHover"
                css={{
                  cursor: 'pointer',
                  transition: TRANSITIONS.standard,
                  opacity: activeTab === id ? 1 : 0.5,
                }}
              >
                <Flex
                  css={{
                    alignItems: 'flex-start',
                    flexDirection: ['column', 'row'],
                  }}
                >
                  <Box css={{ w: [pxToRem(50), pxToRem(80)], h: [pxToRem(50), pxToRem(80)] }}>
                    <Image
                      src={`/static/blueprint/icon_${id}.png`}
                      alt={title}
                      css={{ maxHeight: '100%', maxW: '100%' }}
                    />
                  </Box>
                  <Box
                    position="relative"
                    css={{
                      ml: [0, 4],
                      mt: [3, 0],
                      mb: 2,
                      position: 'relative',
                      '&:after': {
                        position: 'absolute',
                        display: ['block', 'none'],
                        content: '""',
                        bottom: -2,
                        left: 0,
                        w: '100%',
                        h: pxToRem(3),
                        transition: TRANSITIONS.standard,
                        bg: activeTab === id ? 'text' : 'transparent',
                      },
                    }}
                  >
                    <Heading size="2xs">{title}</Heading>
                    <Box css={{ color: 'textSecondary', display: ['none', 'block'] }}>
                      {description}
                    </Box>
                  </Box>
                </Flex>
              </AppLink>
            </SwiperSlide>
          ))}
          <SliderNavigationRounded />
        </SliderWrapper>
      </Container>
    </Box>
  );
}

const BLUEPRINT_ITEMS_INTRO: (BlueprintItem & { stylesContainer: SystemStyleObject })[] = [
  {
    ...BLUEPRINT_ITEMS_BY_ID.vision,
    stylesContainer: { left: 0, transform: 'translate(0%,-130%)', order: 1 },
  },
  {
    ...BLUEPRINT_ITEMS_BY_ID.metaverse,
    stylesContainer: { left: 0, transform: 'translateY(130%)', order: 3 },
  },
  {
    ...BLUEPRINT_ITEMS_BY_ID.irl,
    stylesContainer: {
      left: '50%',
      transform: 'translate(-50%,250%)',
      order: 5,
    },
  },
  {
    ...BLUEPRINT_ITEMS_BY_ID.ecosystem,
    stylesContainer: { right: 0, transform: 'translate(-28%, -130%)', order: 2 },
  },
  {
    ...BLUEPRINT_ITEMS_BY_ID.community,
    stylesContainer: { right: 0, transform: 'translate(0%, 130%)', order: 4 },
  },
  {
    ...BLUEPRINT_ITEMS_BY_ID.social_impact,
    stylesContainer: { left: '50%', transform: 'translate(-50%,-250%)', order: 6 },
  },
];

function IntroSection() {
  return (
    <Flex
      css={{
        height: '100%',
        pt: [pxToRem(125)],
        pb: [pxToRem(60), pxToRem(125)],
        bg: 'url(/static/blueprint/intro_bg.jpg) center/cover no-repeat',
        minHeight: ['auto', pxToRem(790)],
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container position="relative">
        <Heading
          variant="displayPrimary"
          css={{ fontSize: ['7xl', '10xl'], textAlign: 'center', mb: [10, 0] }}
        >
          blueprint
        </Heading>
        <Box
          css={{
            maxWidth: [pxToRem(300), '100%'],
            mx: 'auto',
            display: ['flex', 'block'],
            flexDirection: 'column',
          }}
        >
          {BLUEPRINT_ITEMS_INTRO.map(({ stylesContainer, id, slug, title, description }) => (
            <AppLink
              href={LANDING_ROUTES.BLUEPRINT_TAB(slug)}
              variant="noHover"
              key={id}
              css={{
                height: [pxToRem(50), pxToRem(80)],
                position: ['relative', 'absolute'],
                top: 0,
                mb: [6, 0],
                ...stylesContainer,
                [`@media screen and (max-width: ${pxToRem(767)})`]: {
                  left: 0,
                  right: 0,
                  transform: 'none',
                },
              }}
            >
              <Flex css={{ alignItems: 'center' }}>
                <Image
                  src={`/static/blueprint/icon_${id}.png`}
                  alt={title}
                  css={{
                    animation: `${floatAnimation} 3.5s ease-in-out infinite`,
                    maxHeight: pxToRem(80),
                    maxWidth: [pxToRem(50), '100%'],
                  }}
                />
                <Box ml={4}>
                  <Heading size="2xs">{title}</Heading>
                  <Box css={{ color: 'textSecondary' }}>{description}</Box>
                </Box>
              </Flex>
            </AppLink>
          ))}
        </Box>
      </Container>
    </Flex>
  );
}

export function BlueprintLayout({ tabId }: { tabId?: BlueprintTabId }) {
  const [isNavHidden, setIsNavHidden] = useState(false);

  return (
    <Flex css={{ width: '100%', flexDirection: 'column' }}>
      <PageSEOTags
        pageKey={tabId ? `blueprint.${BLUEPRINT_ITEMS_BY_ID[tabId].slug}` : 'blueprint'}
      />
      {tabId === undefined ? (
        <IntroSection />
      ) : (
        <BlueprintTabs {...{ activeTab: tabId, isNavHidden }} />
      )}
      {tabId === 'ecosystem' ? <EcosystemTab /> : null}
      {tabId === 'metaverse' ? <MetaverseTab /> : null}
      {tabId === 'vision' ? <VisionTab setIsNavHidden={setIsNavHidden} /> : null}
      {tabId === 'irl' ? <IRLTab /> : null}
      {tabId === 'community' ? <CommunityTab /> : null}
      {tabId === 'social_impact' ? <SocialImpactTab /> : null}
    </Flex>
  );
}
